import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Collapse, Pagination } from 'react-bootstrap';
import { showError, showSuccess, showWarning } from '../../helpers/swal';
import templateService from '../../../services/template.service';
import clinicService from '../../../services/clinic.service';
import Loading from '../../common/Loading';

const MEDIUM_BADGE = {
    EMAIL: 'admin-templates-medium-badge admin-templates-medium-badge--email',
    SMS: 'admin-templates-medium-badge admin-templates-medium-badge--sms',
    CALL: 'admin-templates-medium-badge admin-templates-medium-badge--call',
};

const STAT_CONFIG = [
    { key: 'email', label: 'Email', icon: 'bi-envelope', iconClass: 'admin-templates-stat__icon--email' },
    { key: 'sms', label: 'SMS', icon: 'bi-chat-dots', iconClass: 'admin-templates-stat__icon--sms' },
    { key: 'call', label: 'Call', icon: 'bi-telephone', iconClass: 'admin-templates-stat__icon--call' },
    { key: 'total', label: 'Available', icon: 'bi-collection', iconClass: 'admin-templates-stat__icon--total' },
    {
        key: 'selected',
        label: 'Selected',
        icon: 'bi-check2-square',
        iconClass: 'install-templates-stat__icon--selected',
    },
];

const InstallTemplateDashboard = () => {
    const [templates, setTemplates] = useState([]);
    const [clinics, setClinics] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState('');
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [searchPanelOpen, setSearchPanelOpen] = useState(false);
    const [mediumTypeFilter, setMediumTypeFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [installing, setInstalling] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 0, size: 20 });

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [templatesResponse, clinicsResponse] = await Promise.all([
                templateService.getEcxTemplate(0, 1000),
                clinicService.getAllClinicsFast(),
            ]);

            const clinicTemplates = (templatesResponse.data.content || []).filter(
                (template) => template.usedBy === 'CLINIC'
            );

            setTemplates(clinicTemplates);
            setClinics(clinicsResponse.data || []);
            setSelectedTemplates([]);
            setSelectedClinic('');
            setExpandedRowId(null);
            setPagination((prev) => ({ ...prev, page: 0 }));
        } catch (err) {
            console.error('Error fetching install template data:', err);
            setError('Failed to load templates and clinics. Please try again.');
            setTemplates([]);
            setClinics([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const templateCounts = useMemo(
        () => ({
            email: templates.filter((t) => t.mediumType === 'EMAIL').length,
            sms: templates.filter((t) => t.mediumType === 'SMS').length,
            call: templates.filter((t) => t.mediumType === 'CALL').length,
            total: templates.length,
            selected: selectedTemplates.length,
        }),
        [templates, selectedTemplates]
    );

    const filteredTemplates = useMemo(() => {
        const query = appliedSearch.trim().toLowerCase();
        return templates.filter((template) => {
            if (mediumTypeFilter && template.mediumType !== mediumTypeFilter) {
                return false;
            }
            if (!query) {
                return true;
            }
            const name = (template.reminderName || '').toLowerCase();
            const content = (template.content || '').toLowerCase();
            return name.includes(query) || content.includes(query);
        });
    }, [templates, mediumTypeFilter, appliedSearch]);

    const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / pagination.size));
    const currentPage = Math.min(pagination.page, totalPages - 1);
    const pageItems = useMemo(() => {
        const start = currentPage * pagination.size;
        return filteredTemplates.slice(start, start + pagination.size);
    }, [filteredTemplates, currentPage, pagination.size]);

    const selectedClinicName = useMemo(() => {
        const clinic = clinics.find((item) => String(item.id) === String(selectedClinic));
        return clinic?.name || '';
    }, [clinics, selectedClinic]);

    const visibleSelectedCount = pageItems.filter((template) =>
        selectedTemplates.includes(template.id)
    ).length;
    const allVisibleSelected = pageItems.length > 0 && visibleSelectedCount === pageItems.length;
    const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

    const handleSelectAllVisible = (checked) => {
        const visibleIds = pageItems.map((template) => template.id);
        setSelectedTemplates((prev) => {
            if (checked) {
                return [...new Set([...prev, ...visibleIds])];
            }
            return prev.filter((id) => !visibleIds.includes(id));
        });
    };

    const handleTemplateSelect = (templateId) => {
        setSelectedTemplates((prev) =>
            prev.includes(templateId) ? prev.filter((id) => id !== templateId) : [...prev, templateId]
        );
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setAppliedSearch(searchTerm);
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const handleClearFilters = () => {
        setMediumTypeFilter('');
        setSearchTerm('');
        setAppliedSearch('');
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const handleInstallTemplates = async () => {
        if (!selectedClinic) {
            showWarning('Clinic Required', 'Please select a clinic first.');
            return;
        }

        if (selectedTemplates.length === 0) {
            showWarning('Templates Required', 'Please select at least one template to install.');
            return;
        }

        try {
            setInstalling(true);

            await templateService.installTemplates({
                clinicId: selectedClinic,
                templatesList: selectedTemplates,
            });

            await showSuccess(
                'Templates Installed',
                `Successfully installed ${selectedTemplates.length} template(s) to ${selectedClinicName || 'the clinic'}.`,
                { showConfirmButton: true, timer: undefined }
            );

            setSelectedTemplates([]);
            setSelectedClinic('');
        } catch (err) {
            console.error('Error installing templates:', err);
            showError(
                'Installation Failed',
                err.response?.data?.message || 'Failed to install templates. Please try again.'
            );
        } finally {
            setInstalling(false);
        }
    };

    const getPaginationItems = () => {
        const items = [];
        const total = totalPages;
        const maxVisible = 5;

        if (total < 1) {
            return items;
        }

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => setPagination((p) => ({ ...p, page: 0 }))}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage + 1);
        let endPage = Math.min(total - 1, currentPage + 3);

        if (currentPage < 3) {
            endPage = Math.min(total - 1, maxVisible);
        }
        if (currentPage > total - 4) {
            startPage = Math.max(2, total - maxVisible + 1);
        }

        if (startPage > 2) {
            items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => setPagination((p) => ({ ...p, page: number - 1 }))}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < total - 1) {
            items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
        }

        if (total > 1) {
            items.push(
                <Pagination.Item
                    key={total}
                    active={total === currentPage + 1}
                    onClick={() => setPagination((p) => ({ ...p, page: total - 1 }))}
                >
                    {total}
                </Pagination.Item>
            );
        }

        return items;
    };

    const startItem = filteredTemplates.length > 0 ? currentPage * pagination.size + 1 : 0;
    const endItem = Math.min((currentPage + 1) * pagination.size, filteredTemplates.length);

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="pagetitle">
                    <h1>
                        <i className="bi bi-cloud-arrow-down"></i> Install Templates
                    </h1>
                </div>
                <Loading />
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h1>
                    <i className="bi bi-cloud-arrow-down"></i> Install Templates
                </h1>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleInstallTemplates}
                    disabled={!selectedClinic || selectedTemplates.length === 0 || installing}
                >
                    {installing ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Installing...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-cloud-arrow-down me-1"></i>
                            Install Selected ({selectedTemplates.length})
                        </>
                    )}
                </Button>
            </div>

            {error && (
                <Alert variant="warning" className="border-0 shadow-sm mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                </Alert>
            )}

            <Card className="admin-templates-card shadow-sm border-0">
                <Card.Body className="p-3">
                    <div className="admin-templates-stat-grid">
                        {STAT_CONFIG.map((stat) => (
                            <div
                                key={stat.key}
                                className={`admin-templates-stat${stat.key === 'selected' ? ' install-templates-stat--selected' : ''}`}
                            >
                                <div className="admin-templates-stat__info">
                                    <div className="admin-templates-stat__value">{templateCounts[stat.key]}</div>
                                    <div className="admin-templates-stat__label">{stat.label}</div>
                                </div>
                                <span className={`admin-templates-stat__icon ${stat.iconClass}`}>
                                    <i className={`bi ${stat.icon}`}></i>
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="row g-3 mb-4">
                        <div className="col-lg-8">
                            <div className="notifications-panel install-templates-clinic-panel">
                                <div className="notifications-panel__header">
                                    <i className="bi bi-building"></i>
                                    Target Clinic
                                </div>
                                <div className="notifications-panel__body">
                                    <label htmlFor="install-clinic-select" className="notifications-form-label">
                                        Choose clinic *
                                    </label>
                                    <select
                                        id="install-clinic-select"
                                        className="form-select"
                                        value={selectedClinic}
                                        onChange={(event) => setSelectedClinic(event.target.value)}
                                    >
                                        <option value="">Select a clinic</option>
                                        {clinics.map((clinic) => (
                                            <option key={clinic.id} value={clinic.id}>
                                                {clinic.name}
                                                {clinic.city ? ` — ${clinic.city}` : ''}
                                                {clinic.number ? ` (#${clinic.number})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="notifications-form-hint mb-0">
                                        Templates will be copied to the selected clinic&apos;s template library.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="install-templates-summary-panel">
                                <div className="install-templates-summary-panel__label">Ready to install</div>
                                <div className="install-templates-summary-panel__value">
                                    {selectedTemplates.length}
                                </div>
                                <div className="install-templates-summary-panel__meta">
                                    template{selectedTemplates.length !== 1 ? 's' : ''} selected
                                </div>
                                {selectedClinicName ? (
                                    <div className="install-templates-summary-panel__clinic">
                                        <i className="bi bi-hospital me-1"></i>
                                        {selectedClinicName}
                                    </div>
                                ) : (
                                    <div className="install-templates-summary-panel__clinic install-templates-summary-panel__clinic--empty">
                                        No clinic selected
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                        <div className="mb-0 d-flex align-items-center gap-2">
                            <i className="bi bi-journal-text text-primary"></i>
              <h5 className="card-title mb-0 admin-templates-card__title">
                                Clinic Templates
                                <span className="admin-templates-card__count">
                                    ({filteredTemplates.length} available
                                    {appliedSearch || mediumTypeFilter ? ' matching filter' : ''})
                                </span>
                            </h5>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm flex-shrink-0"
                            onClick={() => setSearchPanelOpen((open) => !open)}
                            aria-expanded={searchPanelOpen}
                            title={searchPanelOpen ? 'Hide filters' : 'Show filters'}
                        >
                            <i className="bi bi-funnel me-1"></i>
                            Filter
                        </button>
                    </div>

                    <Collapse in={searchPanelOpen}>
                        <div>
                            <form
                                className="process-queue-filters mb-4 p-3 bg-light rounded border"
                                onSubmit={handleSearchSubmit}
                            >
                                <div className="row g-2 g-md-3 align-items-center process-queue-filters__row">
                                    <div className="col-12 col-md-6 col-lg-4 process-queue-filters__item process-queue-filters__item--search">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text process-queue-filters__label">Search</span>
                                            <input
                                                type="text"
                                                className="form-control process-queue-filters__control"
                                                placeholder="Template name or content"
                                                value={searchTerm}
                                                onChange={(event) => setSearchTerm(event.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3 col-lg-auto process-queue-filters__item">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text process-queue-filters__label">Type</span>
                                            <select
                                                className="form-select form-select-sm process-queue-filters__control"
                                                value={mediumTypeFilter}
                                                onChange={(event) => {
                                                    setMediumTypeFilter(event.target.value);
                                                    setPagination((prev) => ({ ...prev, page: 0 }));
                                                }}
                                            >
                                                <option value="">All types</option>
                                                <option value="EMAIL">Email</option>
                                                <option value="SMS">SMS</option>
                                                <option value="CALL">Call</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-auto process-queue-filters__actions d-flex flex-nowrap gap-2">
                                        <Button type="submit" variant="primary" size="sm">
                                            <i className="bi bi-search me-1"></i>
                                            Apply
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={handleClearFilters}
                                            disabled={!mediumTypeFilter && !appliedSearch && !searchTerm}
                                        >
                                            <i className="bi bi-x-lg me-1"></i>
                                            Clear
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </Collapse>

                    {templates.length === 0 ? (
                        <div className="install-templates-empty">
                            <i className="bi bi-inbox"></i>
                            <h5>No Clinic Templates Available</h5>
                            <p>There are no admin templates marked for clinic use.</p>
                        </div>
                    ) : filteredTemplates.length === 0 ? (
                        <Alert variant="light" className="border mb-0">
                            No templates match your filters.
                        </Alert>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover align-middle mb-0 process-queue-table admin-templates-table install-templates-table">
                                    <thead>
                                        <tr>
                                            <th className="install-templates-table__select-col">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={allVisibleSelected}
                                                    ref={(input) => {
                                                        if (input) {
                                                            input.indeterminate = someVisibleSelected;
                                                        }
                                                    }}
                                                    onChange={(event) => handleSelectAllVisible(event.target.checked)}
                                                    aria-label="Select all visible templates"
                                                />
                                            </th>
                                            <th>
                                                <i className="bi bi-bookmark"></i> Template Name
                                            </th>
                                            <th>
                                                <i className="bi bi-layers"></i> Medium Type
                                            </th>
                                            <th>
                                                <i className="bi bi-chat-left-text"></i> Content
                                            </th>
                                            <th>
                                                <i className="bi bi-calendar-event"></i> Created At
                                            </th>
                                            <th className="text-end">
                                                <i className="bi bi-list-ul"></i> Details
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageItems.map((template) => {
                                            const isSelected = selectedTemplates.includes(template.id);
                                            const isExpanded = expandedRowId === template.id;

                                            return (
                                                <React.Fragment key={template.id}>
                                                    <tr
                                                        className={
                                                            isSelected ? 'install-templates-row--selected' : undefined
                                                        }
                                                    >
                                                        <td className="text-center">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={isSelected}
                                                                onChange={() => handleTemplateSelect(template.id)}
                                                                aria-label={`Select ${template.reminderName || 'template'}`}
                                                            />
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold">
                                                                {template.reminderName || 'Unnamed Template'}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={
                                                                    MEDIUM_BADGE[template.mediumType] ||
                                                                    'admin-templates-medium-badge'
                                                                }
                                                            >
                                                                {template.mediumType || '—'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div
                                                                className="admin-templates-content-preview"
                                                                title={template.content}
                                                            >
                                                                {template.content || '—'}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <small className="text-muted">{template.createdAt || '—'}</small>
                                                        </td>
                                                        <td className="text-end">
                                                            <Button
                                                                variant={
                                                                    isExpanded ? 'outline-secondary' : 'outline-primary'
                                                                }
                                                                size="sm"
                                                                onClick={() => toggleRow(template.id)}
                                                                aria-expanded={isExpanded}
                                                                title={isExpanded ? 'Collapse details' : 'Expand details'}
                                                            >
                                                                <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={6} className="p-0 border-top-0">
                                                            <Collapse in={isExpanded}>
                                                                <div className="admin-templates-detail-panel">
                                                                    <div className="row g-3">
                                                                        <div className="col-md-6">
                                                                            <h6 className="small fw-semibold text-uppercase text-muted mb-2">
                                                                                Template Details
                                                                            </h6>
                                                                            <div className="small mb-2">
                                                                                <strong>ID:</strong>{' '}
                                                                                <span className="font-monospace text-muted">
                                                                                    {template.id}
                                                                                </span>
                                                                            </div>
                                                                            <div className="small mb-2">
                                                                                <strong>Reminder Type ID:</strong>{' '}
                                                                                <span className="font-monospace text-muted">
                                                                                    {template.reminderTypeId || '—'}
                                                                                </span>
                                                                            </div>
                                                                            <div className="small">
                                                                                <strong>Full Content:</strong>
                                                                                <div className="admin-templates-detail-content mt-1">
                                                                                    {template.content || '—'}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <h6 className="small fw-semibold text-uppercase text-muted mb-2">
                                                                                Timestamps
                                                                            </h6>
                                                                            <div className="small mb-2">
                                                                                <strong>Created:</strong>{' '}
                                                                                <span className="text-muted">
                                                                                    {template.createdAt || '—'}
                                                                                </span>
                                                                            </div>
                                                                            <div className="small">
                                                                                <strong>Modified:</strong>{' '}
                                                                                <span className="text-muted">
                                                                                    {template.modifiedAt || '—'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Collapse>
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                                <div className="d-flex flex-wrap align-items-center gap-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="small text-muted">Show</span>
                                        <select
                                            className="form-select form-select-sm admin-templates-page-size"
                                            value={pagination.size}
                                            onChange={(event) =>
                                                setPagination({
                                                    page: 0,
                                                    size: Number(event.target.value),
                                                })
                                            }
                                        >
                                            {[10, 20, 50, 100].map((size) => (
                                                <option key={size} value={size}>
                                                    {size}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="small text-muted">entries</span>
                                    </div>
                                    <span className="text-muted small">
                                        Showing {startItem} to {endItem} of {filteredTemplates.length}
                                    </span>
                                    <span className="install-templates-selected-pill">
                                        {selectedTemplates.length} selected
                                    </span>
                                </div>
                                {totalPages > 1 && (
                                    <Pagination className="mb-0">
                                        <Pagination.First
                                            onClick={() => setPagination((p) => ({ ...p, page: 0 }))}
                                            disabled={currentPage === 0}
                                        />
                                        <Pagination.Prev
                                            onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                                            disabled={currentPage === 0}
                                        />
                                        {getPaginationItems()}
                                        <Pagination.Next
                                            onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                                            disabled={currentPage >= totalPages - 1}
                                        />
                                        <Pagination.Last
                                            onClick={() => setPagination((p) => ({ ...p, page: totalPages - 1 }))}
                                            disabled={currentPage >= totalPages - 1}
                                        />
                                    </Pagination>
                                )}
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default InstallTemplateDashboard;
